import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Table, Tag, Alert, Tooltip, notification } from "antd"
import { LoadingOutlined } from "@ant-design/icons"
import moment from "moment"

import { byClientThenGroup, byGroupThenClient } from "./finalizeSort"

import { formatCurrency } from "../../../lib/utilities"
import {
    downloadRunFiles,
    generateMissingFiles,
    missingFiles,
    regenerateStaleFiles,
    selectFinalizePlan,
    selectRequestedFiles,
    staleFiles,
} from "../clientBatchingSlice"

export default function FinalizeResult() {
    const dispatch = useDispatch()
    const plan = useSelector(selectFinalizePlan)
    const requestedFiles = useSelector(selectRequestedFiles)
    // Which "Download all" zip is being built right now, if any.
    const [downloading, setDownloading] = useState(null)

    if (!plan) return null

    const batches = plan.accounting_batches || []
    const dropped = plan.dropped_bill_ids || []
    const summary = plan.preflight_summary || {}
    const quickbooks = plan.quickbooks_file
    // Counted in FILES: each batch gets an upload file, an invoice PDF and a bill images zip, and any
    // of them can be missing. `pendingFiles` is everything not here yet; `requestable` leaves out the
    // ones already asked for, so the button cannot start a second build of a zip still running.
    const pendingFiles = batches.reduce(
        (count, batch) =>
            count +
            (batch.needs_upload_file ? 1 : 0) +
            (batch.needs_invoice_pdf ? 1 : 0) +
            (batch.needs_bill_images_zip ? 1 : 0),
        0
    )
    const requestable = missingFiles(batches, requestedFiles).length
    // A/R feedback item 2: files built from data that has since changed, not already rebuilding.
    const staleCount = staleFiles(batches, requestedFiles).length

    const handleRegenerateStale = async () => {
        const result = await dispatch(regenerateStaleFiles(batches))
        notification.info({
            message: "Regenerating stale files",
            description: `Requested ${result.requested} file(s). The Stale tags clear as each one finishes.`,
        })
    }

    // Download plus, when the data has moved on since the file was built, a Stale tag.
    const renderFile = (url, record, staleKey) =>
        url ? (
            <>
                <a href={url} target="_blank" rel="noopener noreferrer">
                    Download
                </a>
                {record.stale_files && record.stale_files[staleKey] && (
                    <Tooltip title="Changed since this file was generated. Use Regenerate stale files.">
                        <Tag color="red" style={{ marginLeft: "8px" }}>
                            Stale
                        </Tag>
                    </Tooltip>
                )}
            </>
        ) : (
            <span style={{ opacity: 0.6 }}>Generating…</span>
        )

    // "Download all" in the Upload File and Invoice headers: one zip of that file for every batch,
    // built by the API. Disabled until at least one exists; a tooltip says so if some are still coming.
    const renderDownloadAll = (kind, urlKey) => {
        const ready = batches.filter((batch) => batch[urlKey]).length
        if (downloading === kind) {
            return (
                <span style={{ marginLeft: "8px", fontWeight: "normal" }}>
                    <LoadingOutlined /> Zipping…
                </span>
            )
        }
        if (ready === 0) return null
        const link = (
            <a
                href="javascript:;"
                style={{ marginLeft: "8px", fontWeight: "normal" }}
                onClick={async () => {
                    setDownloading(kind)
                    await dispatch(downloadRunFiles(plan.run.id, kind))
                    setDownloading(null)
                }}
            >
                Download all
            </a>
        )
        return ready < batches.length ? (
            <Tooltip title={`${batches.length - ready} file(s) are still generating; the zip lists them in MISSING.txt.`}>
                {link}
            </Tooltip>
        ) : (
            link
        )
    }

    const handleGenerateMissing = async () => {
        const result = await dispatch(generateMissingFiles(batches))
        notification.info({
            message: "Generating files",
            description: `Requested ${result.requested} file(s). They appear here as each one finishes.`,
        })
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                {/* one wrapper: .card-body is a flex row */}
                <div>
                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>Batches</h2>
                            <div className="badge">{batches.length}</div>
                        </div>

                        <div className="actions">
                            <div className="table-action-links">
                                {requestable > 0 && (
                                    <a href="javascript:;" onClick={handleGenerateMissing}>
                                        Generate missing files ({requestable})
                                    </a>
                                )}
                                {staleCount > 0 && (
                                    <a href="javascript:;" onClick={handleRegenerateStale}>
                                        Regenerate stale files ({staleCount})
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>

                    <p className="p-10">
                        <Tag color="green">Finalized</Tag>
                        {plan.run && plan.run.finalized_at
                            ? ` ${moment(plan.run.finalized_at).format("MMM D, YYYY [at] h:mm A")}`
                            : ""}
                        {" · these batches are now on each client's Accounting Batches screen."}
                    </p>

                    {quickbooks && (
                        <p className="p-10">
                            {`QuickBooks file for ${moment(quickbooks.file_date).format("MMM D, YYYY")}: `}
                            {quickbooks.batch_file_url && !quickbooks.stale ? (
                                <a href={quickbooks.batch_file_url} target="_blank" rel="noopener noreferrer">
                                    Download
                                </a>
                            ) : (
                                // Rebuilt after every finalize; if it never arrives, Regenerate is on
                                // the Today's Runs screen.
                                <span style={{ opacity: 0.6 }}>Generating…</span>
                            )}
                        </p>
                    )}

                    {dropped.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: "10px" }}
                            message={`${dropped.length} bill(s) were dropped at finalize and are not in any of these batches.`}
                            description="They were deleted, or batched by someone else, between the sweep and finalize."
                        />
                    )}

                    {pendingFiles > 0 && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: "10px" }}
                            message={`${pendingFiles} file(s) are still being generated.`}
                            description="They fill in here as each finishes; bill image zips can take a few minutes. If one never arrives, reload this page and use Generate missing files — the batch and its bills are already saved."
                        />
                    )}

                    {/* Pre-sorted as well, so the base order is Client then Batch Group whatever the API sends. */}
                    <Table dataSource={[...batches].sort(byClientThenGroup)} rowKey={(r) => r.id} pagination={false}>
                        {/* Client then Batch Group by default; only these two columns sort. */}
                        <Table.Column
                            title="Client"
                            key="client_name"
                            dataIndex="client_name"
                            sorter={byClientThenGroup}
                            defaultSortOrder="ascend"
                            sortDirections={["ascend", "descend", "ascend"]}
                        />
                        <Table.Column
                            title="Batch Group"
                            key="batch_group_name"
                            dataIndex="batch_group_name"
                            sorter={byGroupThenClient}
                            sortDirections={["ascend", "descend", "ascend"]}
                            render={(text) => text || "No batch group"}
                        />
                        <Table.Column title="Batch Code" key="batch_code" dataIndex="batch_code" />
                        <Table.Column title="Bills" key="bill_count" dataIndex="bill_count" align="right" />
                        <Table.Column
                            title="Batch Total"
                            key="bills_total"
                            dataIndex="bills_total"
                            align="right"
                            render={(text) => formatCurrency(text)}
                        />
                        <Table.Column
                            title={<>Upload File {renderDownloadAll("upload_file", "upload_file_url")}</>}
                            key="file"
                            render={(text, record) => renderFile(record.upload_file_url, record, "upload_file")}
                        />
                        <Table.Column
                            title={<>Invoice {renderDownloadAll("invoice_pdf", "invoice_pdf_url")}</>}
                            key="invoice_pdf"
                            render={(text, record) => renderFile(record.invoice_pdf_url, record, "invoice_pdf")}
                        />
                        <Table.Column
                            title="Bills (zip)"
                            key="bill_images_zip"
                            render={(text, record) => renderFile(record.bill_images_zip_url, record, "bill_images_zip")}
                        />
                    </Table>

                    {summary.summary && (
                        <p className="p-10" style={{ opacity: 0.7 }}>
                            Pre-flight at finalize: {summary.summary.total} finding(s)
                            {summary.summary.worst_severity ? `, worst was ${summary.summary.worst_severity}` : ""}.
                            This is the snapshot taken when the run was committed, not what pre-flight says today.
                        </p>
                    )}
                </div>
            </div>
        </div>
    )
}
